import _ from 'lodash';
import { INCHES_TO_FEET_FACTOR } from 'spw-components/unit-conversion/hooks';
import {
  ICalibration,
  MeasurementState,
} from 'spw-redux/modules/floorplan/types';

export const CALIBRATION_AUTOZOOM_THRESHOLD = -0.4;
export const CALIBRATION_AUTOZOOM_LOW_RESOLUTION_THRESHOLD = 1;

export const isEditingCalibrationState = (state: MeasurementState) =>
  [
    MeasurementState.SET_POINTS,
    MeasurementState.PRE_REDRAW,
    MeasurementState.REDRAW_CONFIRM,
    MeasurementState.SET_DISTANCE,
  ].includes(state);

export const isCalibrationState = (state: MeasurementState) =>
  [
    MeasurementState.COMPLETE,
    MeasurementState.SET_POINTS,
    MeasurementState.SET_DISTANCE,
    MeasurementState.REDRAW_CONFIRM,
    MeasurementState.REQUIRED,
  ].includes(state);

export const isShapeUSFState = (state: MeasurementState) =>
  [
    MeasurementState.CREATE_SPACE_USF,
    MeasurementState.EDIT_SPACE_USF,
    MeasurementState.DISPLAY_SHAPES,
    MeasurementState.DELETE_SHAPE,
  ].includes(state);

export const isUSFMeasurementState = (state: MeasurementState) =>
  [
    MeasurementState.CREATE_SPACE_USF,
    MeasurementState.EDIT_SPACE_USF,
    MeasurementState.EDIT_FLOOR_USF,
    MeasurementState.CREATE_FLOOR_USF,
    MeasurementState.LOADING,
  ].includes(state);

export const isFloorUSFState = (state: MeasurementState) =>
  [
    MeasurementState.INSPECT_FLOOR_USF,
    MeasurementState.EDIT_FLOOR_USF,
    MeasurementState.CREATE_FLOOR_USF,
    MeasurementState.COPYING_MEASURED_FLOOR_USF_STARTED,
  ].includes(state);

export const isCopyMeasuredFloorUsfState = (state: MeasurementState) =>
  [
    MeasurementState.COPYING_MEASURED_FLOOR_USF_STARTED,
    MeasurementState.COPYING_MEASURED_FLOOR_USF_TARGET_FLOOR_SELECTED,
  ].includes(state);

export const isMeasurementState = (state: MeasurementState) =>
  isShapeUSFState(state) || isFloorUSFState(state);

export const calibrationLineExists = (calibration: ICalibration | null) =>
  calibration &&
  !_.isNil(calibration.x1) &&
  !_.isNil(calibration.x2) &&
  !_.isNil(calibration.y1) &&
  !_.isNil(calibration.y2);

// Equation below takes the calibration points on the map, calculates the maps distance between them
// then divides this value by the total number of inches provided by the user,
// returning a 'coordinative distance per inch' value

export const calibrationLineLengthInPixels = (calibration: ICalibration) => {
  if (!calibration) return 0;

  // Pythagorean theorem
  // Calculating hypotenuse
  return Math.sqrt(
    (calibration.x2 - calibration.x1) ** 2 +
      (calibration.y2 - calibration.y1) ** 2
  );
};

export const convertConstantToInches = (calibration: ICalibration) => {
  if (!calibration) return 0;

  const lengthInPixels = calibrationLineLengthInPixels(calibration);
  return lengthInPixels / calibration.constant;
};

// equation below returns a constant value that is 'map points per inch'
export const convertDistanceToConstant = (
  calibration: ICalibration,
  inches: number
) => {
  const lengthInPixels = calibrationLineLengthInPixels(calibration);
  return lengthInPixels / inches;
};

export const convertFeetAndInchesToInches = (feet: number, inches: number) => {
  return (feet || 0) * INCHES_TO_FEET_FACTOR + (inches || 0);
};

export const convertInchesToFeetAndInches = (
  inches: number,
  roundInches = true
) => {
  let remainingInches = inches % INCHES_TO_FEET_FACTOR;
  remainingInches = roundInches ? Math.round(remainingInches) : remainingInches;

  return {
    feet:
      Math.floor(inches / INCHES_TO_FEET_FACTOR) +
      (remainingInches === INCHES_TO_FEET_FACTOR ? 1 : 0),
    inches: remainingInches === INCHES_TO_FEET_FACTOR ? 0 : remainingInches,
  };
};

export const getFeetAndInchesFromConstant = (calibration: ICalibration) => {
  const dist = convertConstantToInches(calibration);
  return convertInchesToFeetAndInches(dist);
};
